import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react'
import { buildingService } from '../services'
import type { Building } from '@popety-io/types'

interface BuildingContextState {
  building: Building | null
  loading: boolean
  error: string | null
  fetchBuilding: (buildingId: string) => Promise<void>
  clearBuilding: () => void
  refetch: () => Promise<void>
}

const BuildingContext = createContext<BuildingContextState | undefined>(
  undefined
)

export const useBuildingContext = () => {
  const context = useContext(BuildingContext)
  if (!context) {
    throw new Error('useBuildingContext must be used within a BuildingProvider')
  }
  return context
}

interface BuildingProviderProps {
  children: ReactNode
}

export const BuildingProvider: React.FC<BuildingProviderProps> = ({
  children,
}) => {
  const [building, setBuilding] = useState<Building | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentBuildingId, setCurrentBuildingId] = useState<string | null>(
    null
  )

  const fetchBuilding = useCallback(async (buildingId: string) => {
    if (!buildingId) {
      setBuilding(null)
      setCurrentBuildingId(null)
      return
    }

    setLoading(true)
    setError(null)
    setCurrentBuildingId(buildingId)

    try {
      const fields = [
        // Basic identifiers
        'id',
        'building_id',
        'egid',
        'egids',
        'address_full',
        'city_name',
        'municipality_name',
        'district_name',
        'region_name',
        'address_postal_code_name',

        // Physical characteristics
        'area',
        'ground_floor_area',
        'gross_floor_area',
        'building_volume',
        'volume_estimate',
        'height',
        'height_estimate',
        'floor_nb',

        // Building details
        'building_year',
        'building_period',
        'renovation_year',
        'renovation_period',
        'category',
        'type',
        'status',
        'protected_rank',
        'buildings_administrative',

        // Energy & heating
        'power_source_heater',
        'power_source_water_heater',
        'heater_generator',
        'additional_heater_generator',
        'energy_reference_area',
        'heat_consumption_index',
        'minergie',
        'heating_type',
        'energy_efficiency',

        // Solar data
        'solar_average_exposure',
        'solar_installed_power',
        'solar_installed_date',
        'solar_area',
        'solar_potential',

        // Views & environment
        'lake_view',
        'mountain_view',
        'exposure',
        'slope',
        'altitude_max_ref',
        'altitude_min_ref',
        'view_quality',

        // Transport & accessibility
        'public_transport_quality',
        'travel_time_to_centers',
        'noise_exposure',
        'distance_to_highvoltage_powerlines',
        'tax_burden',
        'noise_level',
        'transport_accessibility',

        // Dwelling information
        'dwellings_nb',
        'dwellings_total_area',
        'dwelling_median_area',
        'dwelling_median_room_nb',
        'dwelling_min_area',
        'dwelling_max_area',
        'dwelling_min_room_nb',
        'dwelling_max_room_nb',
        'dwelling_last_construction_year',
        'dwellings',

        // Related data
        'lands',
        'lands_owners',
        'lands_owner_list',
        'listings',
        'lands_transactions',
        'lands_building_permits',
        'popety_classification',
        'geo_center',
        'geo_polygon',
        'geo_portal_link',
      ]

      const buildingData = await buildingService.getById(buildingId, fields)

      if (!buildingData.id && !buildingData.building_id) {
        throw new Error('Invalid building data: missing ID')
      }

      setBuilding(buildingData)
    } catch (error) {
      console.error('❌ Error fetching building data:', error)

      let errorMessage = 'Failed to load building data'

      if (error instanceof Error) {
        if (error.message.includes('404')) {
          errorMessage = `Building with ID "${buildingId}" not found`
        } else if (error.message.includes('403')) {
          errorMessage = 'Access denied to building data'
        } else if (error.message.includes('Network')) {
          errorMessage = 'Network error - please check your connection'
        } else {
          errorMessage = `Failed to load building data: ${error.message}`
        }
      }

      setError(errorMessage)
      setBuilding(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const clearBuilding = useCallback(() => {
    setBuilding(null)
    setError(null)
    setCurrentBuildingId(null)
  }, [])

  const refetch = useCallback(async () => {
    if (currentBuildingId) {
      await fetchBuilding(currentBuildingId)
    }
  }, [currentBuildingId, fetchBuilding])

  const contextValue: BuildingContextState = {
    building,
    loading,
    error,
    fetchBuilding,
    clearBuilding,
    refetch,
  }

  return (
    <BuildingContext.Provider value={contextValue}>
      {children}
    </BuildingContext.Provider>
  )
}
